// Ground fog (day-night-and-weather spec, N2 — "light fog, sometimes
// spooky"): on misty days, thin soft patches pool in the low ground —
// lake shores, river bends — weighting toward dawn/dusk and night, with a
// blue-green tint and wandering will-o'-wisps on misty nights.
//
// Client-only: the misty-day roll keys off the calendar day, anchors come
// from water-adjacent visible tiles, intensity from the shipped
// dayNightState.sunHeight. No sim changes, no re-pins.

import type { SceneNode } from '@aod/protocol';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../settings/useSettings.js';
import { tickToGameTime } from '../sim/gameTime.js';
import { dayNightState, nightFactor } from './DayNightLighting.js';
import { hexNoise } from './elevation.js';
import { HEX_DIRS } from './territory.js';

/** ~25% of days are misty. Deterministic per calendar day. */
export function isMistyDay(day: number, year: number): boolean {
  return hexNoise(day * 0.618 + year * 7.7, 0.317) < 0.25;
}

export interface FogAnchor {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Per-anchor opacity/size variation in [0.6, 1]. */
  readonly strength: number;
}

const WATER = new Set(['ocean', 'coast']);

/**
 * Fog anchors: visible land tiles that touch water or carry a river edge —
 * the low, damp ground where mist actually pools. Spread apart (min 2.5
 * world units), capped, deterministic.
 */
export function fogAnchors(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
  visibleCoords: ReadonlySet<string> | null | undefined,
  cap = 14,
): FogAnchor[] {
  const byKey = new Map<string, { q: number; r: number; biome?: string }>();
  for (const tile of tiles) {
    const p = (tile.props ?? {}) as Record<string, unknown>;
    const q = p.q as number | undefined;
    const r = p.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    byKey.set(`${q},${r}`, { q, r, biome: biomeMap.get(`${q},${r}`) });
  }
  const scored: Array<{ t: SceneNode; h: number }> = [];
  for (const tile of tiles) {
    const p = (tile.props ?? {}) as Record<string, unknown>;
    const q = p.q as number | undefined;
    const r = p.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    if (visibleCoords && !visibleCoords.has(`${q},${r}`)) continue;
    const biome = biomeMap.get(`${q},${r}`);
    if (biome !== undefined && WATER.has(biome)) continue; // fog on land, not open water
    const riverEdges = Number(p.riverEdges ?? 0);
    const level = p.level as number | undefined;
    // Mountains at level ≥ 8 anchor fog too — orographic mist pooling in
    // the high valleys (terrain-levels D7). Damp river anchors still win
    // the sort via their higher count weight below.
    const highMountain = biome === 'mountain' && level !== undefined && level >= 8;
    let damp = riverEdges > 0 || highMountain;
    if (!damp) {
      for (let d = 0; d < HEX_DIRS.length && !damp; d++) {
        const n = byKey.get(`${q + HEX_DIRS[d]![0]},${r + HEX_DIRS[d]![1]}`);
        damp = !!n && n.biome !== undefined && WATER.has(n.biome);
      }
    }
    if (!damp) continue;
    // High mountains push themselves to the front of the anchor list.
    const boost = highMountain ? 1 : 0;
    const [x, y, z] = tile.transform.position;
    scored.push({ t: tile, h: hexNoise(x * 1.19, z * 1.23) + boost });
  }
  scored.sort((a, b) => b.h - a.h);
  const out: FogAnchor[] = [];
  for (const { t, h } of scored) {
    const [x, y, z] = t.transform.position;
    if (out.some((a) => Math.hypot(a.x - x, a.z - z) < 2.5)) continue;
    out.push({ x, y, z, strength: 0.6 + h * 0.4 });
    if (out.length >= cap) break;
  }
  return out;
}

/** Soft radial blob — built lazily (no document at import time; bun tests). */
function makeFogTexture(): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,0.9)');
  grad.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

const DAY_MIST = new THREE.Color('#eef2f0');
const NIGHT_MIST = new THREE.Color('#b8ccc8');
const WISP_COLOR = '#b8e0b0';

const MAX_PATCHES = 14;
const WIND_X = 0.83;
const WIND_Z = 0.55; // fixed wind direction (matches the sway lean)
const DRIFT_SPAN = 1.6; // world units the patch slides before wrapping
const FOG_SPEED = 0.1; // tiles/s

export function GroundFog({
  tiles,
  biomeMap,
  visibleCoords,
  currentTick,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
  readonly visibleCoords?: ReadonlySet<string> | null;
  readonly currentTick: number;
}) {
  const showDayNight = useSettings((s) => s.showDayNight);

  const { anchors, misty } = useMemo(() => {
    const cal = tickToGameTime(currentTick);
    const misty = isMistyDay(cal.day, cal.year);
    return { anchors: misty ? fogAnchors(tiles, biomeMap, visibleCoords) : [], misty };
  }, [tiles, biomeMap, visibleCoords, currentTick]);

  const meshRef = useRef<THREE.InstancedMesh>(null);
  const wispRef = useRef<THREE.Points>(null);

  const fogTexture = useMemo(() => makeFogTexture(), []);
  const wispGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
    return g;
  }, []);
  const wispMat = useMemo(
    () =>
      new THREE.PointsMaterial({
        color: WISP_COLOR,
        size: 0.09,
        map: fogTexture,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    [],
  );

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = 0; i < mesh.count; i++) mesh.setMatrixAt(i, zero);
    mesh.instanceMatrix.needsUpdate = true;
  }, [anchors.length]);

  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh || anchors.length === 0) return;
    const t = clock.getElapsedTime();
    // Intensity: misty days only, peaking when the sun is down/dawn-dusk.
    const night = nightFactor(dayNightState.sunHeight);
    const sunDip = 1 - Math.min(1, Math.abs(dayNightState.sunHeight) / 0.8);
    const intensity = Math.min(1, sunDip * 1.2) * (misty ? 1 : 0);
    const tint = NIGHT_MIST.clone().lerp(DAY_MIST, 1 - night);
    const mat = mesh.material as THREE.MeshBasicMaterial;
    mat.color.copy(tint);
    mat.opacity = 0.25 * intensity;
    mesh.visible = mat.opacity > 0.01;

    const obj = new THREE.Object3D();
    anchors.forEach((anchor, i) => {
      const phase = hexNoise(anchor.x, anchor.z);
      const drift = (t * FOG_SPEED + phase * DRIFT_SPAN) % DRIFT_SPAN;
      obj.position.set(
        anchor.x + WIND_X * drift - WIND_X * (DRIFT_SPAN / 2),
        anchor.y + 0.06 + phase * 0.05,
        anchor.z + WIND_Z * drift - WIND_Z * (DRIFT_SPAN / 2),
      );
      const scale = 2.2 * anchor.strength;
      obj.scale.setScalar(scale);
      obj.rotation.set(0, phase * Math.PI, 0);
      obj.updateMatrix();
      mesh.setMatrixAt(i, obj.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;

    // Wisps: two faint lights wandering the fog on misty nights.
    const wisps = wispRef.current;
    if (wisps) {
      const attr = wisps.geometry.getAttribute('position') as THREE.BufferAttribute;
      const spooky = night > 0.5 && misty && intensity > 0.05;
      wispMat.opacity = spooky ? 0.7 : 0;
      wisps.visible = spooky;
      if (spooky) {
        for (let k = 0; k < 2; k++) {
          const anchor = anchors[(k * 5) % anchors.length]!;
          const a = t * 0.4 + k * 2.4;
          attr.setXYZ(
            k,
            anchor.x + Math.cos(a) * 0.5,
            anchor.y + 0.15 + Math.sin(t * 1.3 + k) * 0.05,
            anchor.z + Math.sin(a * 0.8) * 0.5,
          );
        }
        attr.needsUpdate = true;
      }
    }
  });

  if (!showDayNight || !misty || anchors.length === 0) return null;
  return (
    <group>
      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, Math.min(anchors.length, MAX_PATCHES)]}
        frustumCulled={false}
      >
        <planeGeometry args={[2.2, 2.2]} />
        <meshBasicMaterial
          map={fogTexture}
          transparent
          opacity={0}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </instancedMesh>
      <points ref={wispRef} args={[wispGeo, wispMat]} frustumCulled={false} />
    </group>
  );
}
